import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "utils/api";
import Pagination from "components/pagination";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

const PAYMENTS_PAGE_SIZE = 10;

const downloadInvoice = async (booking_id) => {
  try {
    if (!booking_id) {
      console.log("fd:", booking_id);
      toast.error("Invalid booking ID");
      return;
    }

    const res = await api.get(`/admin/booking/${booking_id}/invoice`, {
      responseType: "blob",
    });

    console.log("Invoice response:", res);

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `invoice_${booking_id}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error("Download error:", err.response || err);
    toast.error("Failed to download invoice");
  }
};

const OrdersTable = () => {
  const [payments, setPayments] = useState([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  /* Load payments */
  const loadPayments = async () => {
    try {
      const res = await api.get("/list_payment");
      console.log("API RESPONSE ", res.data);
      setPayments(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payment list");
    }
  };
  const headers = [
    { label: "Payment ID", key: "id" },
    { label: "Order ID", key: "ref_id" },
    { label: "User Name", key: "user_name" },
    { label: "Amount", key: "bill_amount" },
    { label: "Payment Date", key: "payment_date" },
    { label: "Method", key: "payment_method" },
    { label: "Status", key: "payment_status" },
  ];

  const csvData = payments.map((p) => ({
    id: p.id,
    order_id: p.ref_id,
    user_name: p.user_name,
    bill_amount: p.bill_amount,
    payment_date: p.payment_date,
    payment_method:
      p.payment_mode === 1
        ? "UPI"
        : p.payment_mode === 2
        ? "Net Banking"
        : p.payment_mode === 3
        ? "Debit / Credit Card"
        : p.payment_mode === 4
        ? "Net Banking"
        : p.payment_mode === 5
        ? "Cash on Delivery"
        : "",
    payment_status: p.status === 1 ? "Success" : "Failed",
  }));

  useEffect(() => {
    loadPayments();
  }, []);
  const USERS_PAGE_SIZE = 10;
  const [usersPage, setUsersPage] = useState(1);
  const filteredUsers = (payments || []).filter((p) => {
    const term = searchTerm.toLowerCase();
    return (
      p.id?.toLowerCase().includes(term) ||
      p.ref_id?.toLowerCase().includes(term) ||
      p.user_name?.toLowerCase().includes(term) ||
      p.bill_amount?.toString().toLowerCase().includes(term) ||
      p.payment_date?.toLowerCase().includes(term) ||
      (p.payment_mode === 1
        ? "upi"
        : p.payment_mode === 2
        ? "net banking"
        : p.payment_mode === 3
        ? "debit/credit card"
        : p.payment_mode === 4
        ? "net banking"
        : p.payment_mode === 5
        ? "cash on delivery"
        : ""
      ).includes(term) ||
      (p.status === 1 ? "success" : "failed").includes(term)
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / USERS_PAGE_SIZE)
  );

  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * USERS_PAGE_SIZE,
    usersPage * USERS_PAGE_SIZE
  );
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 hover:shadow-xl">
        <div className="mb-4 flex flex-col items-center justify-between sm:flex-row">
          <h3 className="text-xl font-bold">Payment List</h3>
          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setUsersPage(1);
                }}
                className="w-full rounded-lg border px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <CSVLink
              data={csvData}
              headers={headers}
              filename={`payments_page_${page}.csv`}
              className="border-slate-200 flex w-full items-center gap-2 rounded-xl border bg-green-500 px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:bg-green-700 sm:w-auto"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 15V3" />
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="m7 10 5 5 5-5" />
              </svg>
              Export
            </CSVLink>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto text-sm sm:table-fixed">
            <thead>
              <tr className="border-b text-center">
                <th className="py-5 text-xs sm:text-sm">Payment ID</th>
                <th className="py-5 text-xs sm:text-sm">Order ID</th>
                <th className="py-5 text-xs sm:text-sm">User Name</th>
                <th className="py-5 text-xs sm:text-sm">Amount</th>
                <th className="py-5 text-xs sm:text-sm">Payment Date</th>
                <th className="py-5 text-xs sm:text-sm">Method</th>
                <th className="py-5 text-xs sm:text-sm">Status</th>
                <th className="py-5 text-xs sm:text-sm">Invoice Bill</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-4 text-center text-gray-400">
                    No payments found
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((p, index) => (
                  <tr key={p.id || index} className="border-b text-center">
                    <td className="py-5">
                      <td className="flex items-center justify-center gap-2 py-3">
                        <span>
                          {p.revealed ? p.id : p.id.slice(0, 4) + "********"}
                        </span>
                        <span
                          className="cursor-pointer text-blue-500"
                          onClick={() => {
                            p.revealed = !p.revealed;
                            setPayments([...payments]);
                          }}
                          title={p.revealed ? "Hide ID" : "Reveal ID"}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </span>
                        <span
                          className="cursor-pointer text-green-500"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(p.id)
                              .then(() =>
                                toast.success("ID copied to clipboard!")
                              )
                              .catch((err) =>
                                console.error("Copy failed", err)
                              );
                          }}
                          title="Copy ID"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="14"
                              height="14"
                              x="8"
                              y="8"
                              rx="2"
                              ry="2"
                            ></rect>
                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                          </svg>
                        </span>
                      </td>
                    </td>

                    <td className="py-5">
                      <td className="flex items-center justify-center gap-2 py-3">
                        <span
                          className="cursor-pointer text-blue-600 hover:underline"
                          onClick={() =>
                            navigate(`/admin/booking/${p.order_id}`)
                          }
                        >
                          {p.orderRevealed
                            ? p.ref_id
                            : p.ref_id.slice(0, 4) + "******"}
                        </span>

                        <span
                          className="cursor-pointer text-blue-500"
                          onClick={() => {
                            p.orderRevealed = !p.orderRevealed;
                            setPayments([...payments]);
                          }}
                          title={
                            p.orderRevealed
                              ? "Hide Order ID"
                              : "Reveal Order ID"
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </span>
                        <span
                          className="cursor-pointer text-green-500"
                          onClick={() => {
                            navigator.clipboard
                              .writeText(p.ref_id)
                              .then(() =>
                                toast.success("Order ID copied to clipboard!")
                              )
                              .catch((err) =>
                                console.error("Copy failed", err)
                              );
                          }}
                          title="Copy Order ID"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <rect
                              width="14"
                              height="14"
                              x="8"
                              y="8"
                              rx="2"
                              ry="2"
                            ></rect>
                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                          </svg>
                        </span>
                      </td>
                    </td>
                    <td className="py-5">{p.user_name}</td>
                    <td className="py-5">₹{p.bill_amount}</td>
                    <td className="py-5">{p.payment_date}</td>

                    <td className="py-5">
                      {p.payment_mode === 1 && "UPI"}
                      {p.payment_mode === 2 && "Net Banking"}
                      {p.payment_mode === 3 && "Debit / Credit Card"}
                      {p.payment_mode === 4 && "Net Banking"}
                      {p.payment_mode === 5 && "Cash on Delivery"}
                    </td>

                    <td
                      className={`py-5 ${
                        p.status === 1
                          ? "font-semibold text-green-600"
                          : "font-semibold text-red-600"
                      }`}
                    >
                      {p.status === 1 ? "Success" : "Failed"}
                    </td>
                    <td className="py-5">
                      <button
                        onClick={() => downloadInvoice(p.booking_id)}
                        className={`rounded px-3 py-1 text-white ${
                          p.status === 0
                            ? "cursor-not-allowed bg-gray-400"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                        disabled={p.status === 0}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filteredUsers.length || 0}
            pageSize={PAYMENTS_PAGE_SIZE}
            onPageChange={setPage}
            itemName="payments"
            className="sm:mx-auto"
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;
